/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Dashboard v1.0.2 - Sección de Multimedia y Almacenamiento
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file MultimediaSection.tsx
 * @description Control de multimedia (imágenes, audios, docs) y estadísticas
 * @version 1.0.2
 * @date 2024-11-03
 * @architecture Frontend → Proxy → WASP
 * 
 * FUNCIONALIDADES:
 * ├─ Estadísticas de media (totales)
 * ├─ Historial de multimedia por contacto
 * ├─ Envío de archivos multimedia
 * ├─ Configuración de auto-descarga
 * └─ Control de calidad de imágenes
 * 
 * ENDPOINTS USADOS:
 * - GET  /api/whatsapp/wasp/media/stats
 * - GET  /api/whatsapp/wasp/media/history/:id
 * - GET  /api/whatsapp/wasp/voice/history/:id
 * - POST /api/whatsapp/wasp/media/send
 * 
 * RESPONSE ESTRUCTURA:
 * {
 *   media: { total, byType: { images, videos, documents, stickers }, totalSize, lastUpdate },
 *   audio: { total, byType: { voice, audio }, totalDuration, totalSize, averageDuration, lastUpdate }
 * }
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Form, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage,
  faMusic,
  faFileVideo,
  faFile,
  faChartBar,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { getHatateToken } from '../../helpers/utils';

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

interface MediaStats {
  media: {
    total: number;
    byType: {
      images: number;
      videos: number;
      documents: number;
      stickers: number;
    };
    totalSize: number;
    lastUpdate: string;
  };
  audio: {
    total: number;
    byType: {
      voice: number;
      audio: number;
    };
    totalDuration: number;
    totalSize: number;
    averageDuration: string;
    lastUpdate: string;
  };
}

interface MultimediaSectionProps {
  user: any;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE
// ═══════════════════════════════════════════════════════════════════════════

const MultimediaSection: React.FC<MultimediaSectionProps> = ({ user }) => {
  
  // Estados
  const [stats, setStats] = useState<MediaStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Configuración
  const [autoDownload, setAutoDownload] = useState(true);
  const [imageQuality, setImageQuality] = useState<'original' | 'compressed'>('compressed');
  const [savingConfig, setSavingConfig] = useState(false);
  const apiUrl = "https://backend.anubisai.net/api/";

  // ═══════════════════════════════════════════════════════════════════════
  // HELPER: Request Config
  // ═══════════════════════════════════════════════════════════════════════

  const getRequestConfig = () => ({
    headers: { Authorization: getHatateToken() },
    mlStuff: {
      whatsAppServerPort: user.mlStuff?.whatsAppServerPort,
      whatsAppApiPort: user.mlStuff?.whatsAppApiPort,
      mlServerPort: user.mlStuff?.mlServerPort
    }
  });

  // ═══════════════════════════════════════════════════════════════════════
  // FETCH STATS
  // ═══════════════════════════════════════════════════════════════════════

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiUrl}whatsapp/wasp/media/stats`,
        getRequestConfig()
      );

      if (response.data.success) {
        setStats(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // SAVE CONFIG (Mock - endpoint no disponible)
  // ═══════════════════════════════════════════════════════════════════════

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    setSuccess('Configuración guardada (local)');
    setTimeout(() => {
      setSavingConfig(false);
    }, 1000);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ═══════════════════════════════════════════════════════════════════════
  // EFECTOS
  // ═══════════════════════════════════════════════════════════════════════

  useEffect(() => {
    fetchStats();
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════════════

  const renderMediaCard = (
    icon: any,
    title: string,
    total: number,
    color: string,
    subtitle?: string
  ) => (
    <Card style={{ 
      border: 'none', 
      borderRadius: '12px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)'
    }}>
      <Card.Body>
        <div className="d-flex align-items-center gap-3">
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: `${color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: color
          }}>
            <FontAwesomeIcon icon={icon} />
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
              {title}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>
              {total}
            </div>
            {subtitle && (
              <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)' }}>
                {subtitle}
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <Card style={{ 
      border: 'none', 
      borderRadius: '12px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
      backgroundColor: 'var(--bs-body-bg)',
      marginBottom: '24px'
    }}>
      <Card.Header style={{ 
        backgroundColor: 'var(--bs-body-bg)', 
        borderBottom: '1px solid var(--bs-border-color)', 
        padding: '16px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faImage} style={{ color: '#3b82f6' }} />
          <h5 className="mb-0" style={{ fontWeight: 600 }}>Multimedia y Almacenamiento</h5>
        </div>
        <Button variant="link" size="sm" onClick={fetchStats} disabled={loading}>
          {loading ? <Spinner size="sm" /> : 'Actualizar'}
        </Button>
      </Card.Header>

      <Card.Body style={{ padding: '24px' }}>
        
        {/* Alertas */}
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        {loading && !stats ? (
          <div className="text-center py-4">
            <Spinner />
          </div>
        ) : (
          <>
            {/* Estadísticas de Media */}
            <div className="mb-4">
              <h6 style={{ fontWeight: 600, marginBottom: '16px' }}>
                <FontAwesomeIcon icon={faChartBar} className="me-2" />
                Estadísticas de Multimedia
              </h6>
              <Row>
                <Col md={3} className="mb-3">
                  {renderMediaCard(
                    faImage,
                    'Imágenes',
                    stats?.media?.byType?.images || 0,
                    '#3b82f6'
                  )}
                </Col>
                <Col md={3} className="mb-3">
                  {renderMediaCard(
                    faFileVideo,
                    'Videos',
                    stats?.media?.byType?.videos || 0,
                    '#ef4444'
                  )}
                </Col>
                <Col md={3} className="mb-3">
                  {renderMediaCard(
                    faMusic,
                    'Audios',
                    stats?.audio?.total || 0,
                    '#10b981',
                    stats?.audio?.total && stats.audio.total > 0 
                      ? `${formatDuration(stats.audio.totalDuration)} total`
                      : undefined
                  )}
                </Col>
                <Col md={3} className="mb-3">
                  {renderMediaCard(
                    faFile,
                    'Documentos',
                    stats?.media?.byType?.documents || 0,
                    '#f59e0b'
                  )}
                </Col>
              </Row>
            </div>

            {/* Almacenamiento */}
            <div className="mb-4 p-3" style={{ 
              backgroundColor: 'var(--bs-light)', 
              borderRadius: '8px' 
            }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span style={{ fontWeight: 600 }}>Almacenamiento Utilizado</span>
                <span className="text-muted">
                  {formatBytes((stats?.media?.totalSize || 0) + (stats?.audio?.totalSize || 0))}
                </span>
              </div>
              <ProgressBar 
                now={25} 
                variant="success"
                style={{ height: '8px' }}
              />
              <small className="text-muted">
                {stats?.media?.total || 0} archivos multimedia • {stats?.audio?.total || 0} audios
              </small>
            </div>

            {/* Detalles de Audio */}
            {stats?.audio && stats.audio.total > 0 && (
              <div className="mb-4 p-3" style={{ 
                backgroundColor: 'var(--bs-body-bg)', 
                border: '1px solid var(--bs-border-color)',
                borderRadius: '8px' 
              }}>
                <h6 style={{ fontWeight: 600, marginBottom: '12px' }}>Detalles de Audio</h6>
                <Row>
                  <Col md={4}>
                    <small className="text-muted d-block">Mensajes de Voz</small>
                    <strong>{stats.audio.byType.voice}</strong>
                  </Col>
                  <Col md={4}>
                    <small className="text-muted d-block">Archivos de Audio</small>
                    <strong>{stats.audio.byType.audio}</strong>
                  </Col>
                  <Col md={4}>
                    <small className="text-muted d-block">Duración Promedio</small>
                    <strong>{stats.audio.averageDuration}s</strong>
                  </Col>
                </Row>
              </div>
            )}

            {/* Configuración */}
            <div>
              <h6 style={{ fontWeight: 600, marginBottom: '16px' }}>
                <FontAwesomeIcon icon={faCog} className="me-2" />
                Configuración
              </h6>

              <div className="mb-3 pb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Auto-descarga de Multimedia</strong>
                    <p style={{ fontSize: '0.9rem', color: 'var(--bs-secondary)', marginBottom: 0 }}>
                      Descargar automáticamente imágenes y archivos recibidos
                    </p>
                  </div>
                  <Form.Check
                    type="switch"
                    id="switch-autodownload"
                    checked={autoDownload}
                    onChange={(e) => setAutoDownload(e.target.checked)}
                    style={{ fontSize: '1.5rem' }}
                  />
                </div>
              </div>

              <div className="mb-3">
                <Form.Group>
                  <Form.Label><strong>Calidad de Imágenes</strong></Form.Label>
                  <Form.Select 
                    value={imageQuality}
                    onChange={(e) => setImageQuality(e.target.value as 'original' | 'compressed')}
                  >
                    <option value="original">Original (Mayor calidad, más espacio)</option>
                    <option value="compressed">Comprimida (Menor calidad, ahorra espacio)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Las imágenes comprimidas usan ~70% menos espacio
                  </Form.Text>
                </Form.Group>
              </div>

              <Button 
                variant="primary"
                onClick={handleSaveConfig}
                disabled={savingConfig}
              >
                {savingConfig ? (
                  <><Spinner size="sm" className="me-2" />Guardando...</>
                ) : (
                  'Guardar Configuración'
                )}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default MultimediaSection;